// EXECUTOR-WRITE-1 Phase 4R (s144, D-144) — executor plan-view row chrome.
//
// Per D-144 the executor edits the SAME form fields inline (that lives in GenericCategoryPage +
// ExpandableSectionLayout, not here). This file is now just the row-level executor chrome that has
// no planner equivalent on the read-only surface: remove/undo an existing record, Save/Cancel a
// "record you found", and the section-level "Add a record you found" button. All are presentational
// or thin mutations; the gate (executor + readOnly + verified death) is decided by the caller.
import { Trash2, Plus, Loader2 } from "lucide-react";
import { useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { SAGE, WARM } from "@shared/brand";

const DESTRUCTIVE = "hsl(var(--destructive))";

/** "Remove this record" in the drawer footer of a LIVE row (soft-delete, Option-B change table). The
 *  removed state itself (dimmed line + Undo) is rendered at the ROW level by ExpandableSectionLayout
 *  once removed, so this component only handles the live→removed transition. */
export function ExecutorRowActions({
  clientId, sectionSlug, rowId, fieldKey, onChanged,
}: {
  clientId: string;
  sectionSlug: string;
  rowId: string;
  fieldKey?: string | null;
  onChanged: () => void;
}) {
  const del = useMutation({
    mutationFn: async () =>
      (await apiRequest("POST", `/api/executor/${clientId}/row/${rowId}/delete`, { section: sectionSlug, fieldKey: fieldKey ?? undefined })).json(),
    onSuccess: onChanged,
  });

  return (
    <button
      type="button" onClick={() => del.mutate()} disabled={del.isPending}
      className="flex items-center gap-1 text-xs font-medium" style={{ color: DESTRUCTIVE }}
      data-testid={`exec-row-delete-${rowId}`}
    >
      {del.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />} Remove this record
    </button>
  );
}

/** Save / Cancel chrome for a pending "record you found" (a blank row filled inline). The values live
 *  in the host page's local-edit state, so save/cancel are host callbacks. */
export function ExecutorPendingRowActions({
  rowId, onSave, onCancel, saving,
}: {
  rowId: string;
  onSave: () => void;
  onCancel: () => void;
  saving: boolean;
}) {
  return (
    <div className="flex items-center gap-3" data-testid={`exec-pending-actions-${rowId}`}>
      <button
        type="button" onClick={onSave} disabled={saving}
        className="rounded-md px-3 py-1 text-xs font-semibold text-white inline-flex items-center gap-1 disabled:opacity-50"
        style={{ backgroundColor: SAGE[600] }} data-testid={`exec-pending-save-${rowId}`}
      >
        {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Save new record
      </button>
      <button type="button" onClick={onCancel} className="text-xs" style={{ color: WARM.taupe }} data-testid={`exec-pending-cancel-${rowId}`}>
        Cancel
      </button>
    </div>
  );
}

/** Section-level add — appends a blank pending row (host state) the executor fills inline with the
 *  section's normal widgets. F3 (D-144 binder principle): uses the PLANNER's exact add-row wording
 *  ("Add {recordNoun}", e.g. "Add account"), not an executor-invented label. */
export function ExecutorAddButton({ sectionSlug, recordNoun, onClick }: { sectionSlug: string; recordNoun?: string; onClick: () => void }) {
  return (
    <button
      type="button" onClick={onClick}
      className="inline-flex items-center gap-1 text-xs font-medium mt-2" style={{ color: SAGE[600] }}
      data-testid={`exec-add-record-${sectionSlug}`}
    >
      <Plus className="h-3.5 w-3.5" /> Add {recordNoun ?? "record"}
    </button>
  );
}
